<template>
  <div
    ref="root"
    class="tutorial-tooltip"
    :class="[`tutorial-tooltip--${resolvedPlacement}`]"
    :style="tooltipStyle"
  >
    <div
      class="tutorial-tooltip__arrow"
      :class="[`tutorial-tooltip__arrow--${resolvedPlacement}`]"
    />

    <div
      v-if="totalSteps > 1"
      class="tutorial-tooltip__header"
    >
      <span class="tutorial-tooltip__step-indicator">
        {{ currentStep }} de {{ totalSteps }}
      </span>
    </div>

    <h3 class="tutorial-tooltip__title">
      {{ title }}
    </h3>

    <p class="tutorial-tooltip__description">
      {{ description }}
    </p>

    <div class="tutorial-tooltip__actions">
      <template v-if="isInfoBox">
        <button
          type="button"
          class="tutorial-tooltip__btn tutorial-tooltip__btn--next"
          @click="$emit('next')"
        >
          Entendi
        </button>
      </template>

      <template v-else>
        <button
          type="button"
          class="tutorial-tooltip__btn tutorial-tooltip__btn--skip"
          @click="$emit('skip')"
        >
          Pular Tutorial
        </button>

        <button
          v-if="currentStep !== 1 || !requiresClick || !hasTarget"
          type="button"
          class="tutorial-tooltip__btn tutorial-tooltip__btn--next"
          @click="$emit('next')"
        >
          {{ isLastStep ? 'Concluir' : 'Próximo' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
const TOOLTIP_GAP = 16;
const TOOLTIP_WIDTH = 360;
const TOOLTIP_HEIGHT = 180;

export default {
  name: "TutorialTooltip",
  props: {
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: "bottom",
      validator: (value) => ["top", "bottom", "left", "right"].includes(value),
    },
    currentStep: {
      type: Number,
      required: true,
    },
    totalSteps: {
      type: Number,
      required: true,
    },
    requiresClick: {
      type: Boolean,
      default: false,
    },
    targetRect: {
      type: Object,
      default: null,
    },
    hasTarget: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["next", "skip"],
  data() {
    return {
      size: { width: TOOLTIP_WIDTH, height: TOOLTIP_HEIGHT },
      resizeObserver: null,
    };
  },
  computed: {
    isInfoBox() {
      return this.totalSteps === 1;
    },
    isLastStep() {
      return this.currentStep === this.totalSteps;
    },
    resolvedPlacement() {
      if (!this.targetRect || this.targetRect.width === 0) return this.position;

      const fallbackOrder = {
        right: ["right", "left", "bottom", "top"],
        left: ["left", "right", "bottom", "top"],
        bottom: ["bottom", "top", "right", "left"],
        top: ["top", "bottom", "right", "left"],
      };

      return (
        fallbackOrder[this.position].find((pos) => this.positionFits(pos)) ||
        this.position
      );
    },
    tooltipStyle() {
      if (!this.targetRect || this.targetRect.width === 0) {
        return {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
      }

      const gap = TOOLTIP_GAP;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const calculated = this.calculatePosition(this.resolvedPlacement);

      let top = calculated.top;
      let left = calculated.left;

      if (left < gap) left = gap;
      if (left + this.size.width > viewportWidth - gap) {
        left = viewportWidth - this.size.width - gap;
      }
      if (top < gap) top = gap;
      if (top + this.size.height > viewportHeight - gap) {
        top = viewportHeight - this.size.height - gap;
      }

      return {
        top: `${top}px`,
        left: `${left}px`,
      };
    },
  },
  mounted() {
    this.measure();

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.$refs.root);
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    measure() {
      const element = this.$refs.root;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      if (rect.height > 0) {
        this.size = { width: rect.width, height: rect.height };
      }
    },
    calculatePosition(placement) {
      const gap = TOOLTIP_GAP;

      switch (placement) {
      case "top":
        return {
          top: this.targetRect.top - this.size.height - gap,
          left: this.targetRect.left + this.targetRect.width / 2 - this.size.width / 2,
        };
      case "bottom":
        return {
          top: this.targetRect.bottom + gap,
          left: this.targetRect.left + this.targetRect.width / 2 - this.size.width / 2,
        };
      case "left":
        return {
          top: this.targetRect.top + this.targetRect.height / 2 - this.size.height / 2,
          left: this.targetRect.left - this.size.width - gap,
        };
      default:
        return {
          top: this.targetRect.top + this.targetRect.height / 2 - this.size.height / 2,
          left: this.targetRect.right + gap,
        };
      }
    },
    positionFits(placement) {
      const gap = TOOLTIP_GAP;
      const calc = this.calculatePosition(placement);

      return (
        calc.top >= gap &&
        calc.top + this.size.height <= window.innerHeight - gap &&
        calc.left >= gap &&
        calc.left + this.size.width <= window.innerWidth - gap
      );
    },
  },
};
</script>

<style scoped>
.tutorial-tooltip {
  position: fixed;
  z-index: 10001;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 360px;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: auto;
  transition: top 0.18s ease, left 0.18s ease;
}

.tutorial-tooltip__arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 12px solid transparent;
}

.tutorial-tooltip__arrow--top {
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  border-top-color: #ffffff;
  border-bottom: none;
}

.tutorial-tooltip__arrow--bottom {
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  border-bottom-color: #ffffff;
  border-top: none;
}

.tutorial-tooltip__arrow--left {
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  border-left-color: #ffffff;
  border-right: none;
}

.tutorial-tooltip__arrow--right {
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
  border-right-color: #ffffff;
  border-left: none;
}

.tutorial-tooltip__header {
  margin-bottom: 12px;
}

.tutorial-tooltip__step-indicator {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

.tutorial-tooltip__title {
  font-size: 18px;
  font-weight: 600;
  color: #4f3f82;
  margin: 0 0 12px 0;
}

.tutorial-tooltip__description {
  font-size: 14px;
  color: #484848;
  line-height: 1.5;
  margin: 0 0 20px 0;
}

.tutorial-tooltip__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.tutorial-tooltip__btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.tutorial-tooltip__btn--skip {
  background: transparent;
  color: #888;
}

.tutorial-tooltip__btn--skip:hover {
  color: #666;
  background: #f5f5f5;
}

.tutorial-tooltip__btn--next {
  background: #4f3f82;
  color: #ffffff;
}

.tutorial-tooltip__btn--next:hover {
  background: #3d3066;
}
</style>
